import React, { useState, useRef } from 'react';
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import { motion } from 'framer-motion';
import SearchForm from './components/SearchForm';
import ResearchReport from './components/ResearchReport';
import Header from './components/Header';
import Footer from './components/Footer';
import { performOsintResearch } from './services/osint';
import './App.css';

function App() {
  const [searchResults, setSearchResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const historyRef = useRef(null);

  const handleSearch = async (searchData, apiConfig) => {
    setIsLoading(true);
    setSearchResults(null);
    
    try {
      // Perform actual OSINT research
      const result = await performOsintResearch(searchData, apiConfig);
      
      const report = {
        name: searchData.name,
        location: searchData.location,
        company: searchData.company,
        email: searchData.email,
        phone: searchData.phone,
        score: result.score,
        assessment: result.assessment,
        timestamp: new Date().toISOString(),
        stories: result.stories,
        redditPosts: result.redditPosts || [],
        images: result.images || [],
        socialMedia: result.socialMedia,
        contactInfo: result.contactInfo,
        workHistory: [], // Hard to get without paid API
        education: [], // Hard to get without paid API
        redFlags: result.redFlags,
        finalAssessment: generateFinalAssessment(
          result.score, 
          result.stories.length > 0,
          result.redditPosts && result.redditPosts.length > 0,
          result.images && result.images.length > 0,
          Object.keys(result.socialMedia).length > 0
        )
      };
      
      // Add search to history if we have access to the history component
      if (historyRef.current && historyRef.current.addSearchToHistory) {
        historyRef.current.addSearchToHistory(searchData, report);
      }
      
      setSearchResults(report);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const generateFinalAssessment = (score, hasStories, hasRedditPosts, hasImages, hasSocialMedia) => {
    const assessments = [];
    
    if (score >= 70) {
      assessments.push('Significant public digital footprint verified.');
      assessments.push('Multiple data points confirm existence.');
      
      if (hasSocialMedia) {
        assessments.push('Social media profiles detected across multiple platforms.');
      }
      if (hasRedditPosts) {
        assessments.push('Reddit activity found - indicates active online presence.');
      }
      if (hasImages) {
        assessments.push('Visual evidence found in image search.');
      } else if (hasStories) {
        assessments.push('Text records found across multiple sources.');
      }
      
      assessments.push('High confidence in identity authenticity.');
    } else if (score >= 30) {
      assessments.push('Some public records found.');
      assessments.push('Digital footprint exists but is limited.');
      
      if (hasSocialMedia) {
        assessments.push('Limited social media presence detected.');
      }
      if (hasRedditPosts) {
        assessments.push('Some Reddit activity found.');
      }
      if (hasImages) {
        assessments.push('Profile images located.');
      } else if (hasStories) {
        assessments.push('Text-based information available.');
      }
      
      assessments.push('Identity is likely real but maintains privacy.');
    } else {
      assessments.push('Minimal or no public data found.');
      assessments.push('Could be a private individual or fake identity.');
      assessments.push('Without API keys, search scope is limited to Gravatar/Demo.');
      
      if (!hasImages && !hasStories && !hasSocialMedia && !hasRedditPosts) {
        assessments.push('No images, text, or social media data available.');
      }
      
      assessments.push('Recommend further manual verification.');
    }
    
    return assessments;
  };

  return (
    <Router>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <Routes>
            <Route 
              path="/" 
              element={
                <motion.div 
                  initial={{ opacity: 0, y: 20 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  transition={{ duration: 0.6 }}
                >
                  <SearchForm 
                    onSearch={handleSearch} 
                    isLoading={isLoading} 
                    ref={historyRef}
                  />
                  {searchResults && (
                    <motion.div 
                      initial={{ opacity: 0, y: 30 }} 
                      animate={{ opacity: 1, y: 0 }} 
                      transition={{ duration: 0.8, delay: 0.2 }} 
                      className="mt-12"
                    >
                      <ResearchReport data={searchResults} />
                    </motion.div>
                  )}
                </motion.div>
              } 
            />
          </Routes>
        </main>
        <Footer />
      </div>
    </Router>
  );
}

export default App;